import {
  CONTEXT_CHANGE_EVENT,
  clearStaffAppContext,
  filterLocations,
  locationLabel,
  notifyStaffContextChanged,
  readStaffAppContext,
  selectStaffLocation,
  switchStaffPersona,
  type PreviewPersona,
  type StaffAppContext,
} from '../clinic/seed';
import {
  STAFF_NAV_GROUPS,
  STAFF_UTILITY_ITEMS,
  type NavGroupId,
  type StaffNavItem,
} from '../clinic/nav';
import { el } from '../dom';
import { buildLogoutUrl, staffLogoutUri } from '../staff/authorize';
import { STAFF_DOOR } from '../staff/endpoints';
import { clearStaffSession, hasStaffSession, markStaffSignedOut } from '../staff/token';
import { renderStatusHeaderBadge } from '../views/status';
import { navIcon } from './icons';

const collapsed = new Set<NavGroupId>(
  STAFF_NAV_GROUPS.filter((group) => group.collapsedByDefault).map((group) => group.id),
);

export function currentHashPath(): string {
  return window.location.hash.replace(/^#/, '') || '/';
}

export function signOutStaffApp(): void {
  const hosted = hasStaffSession();
  clearStaffAppContext();
  clearStaffSession();
  if (hosted) {
    markStaffSignedOut();
    window.location.assign(
      buildLogoutUrl({
        clientId: STAFF_DOOR.clientId,
        logoutUri: staffLogoutUri(),
      }),
    );
    return;
  }
  window.location.hash = '/';
}

export function renderStaffAppShell(
  host: HTMLElement,
  outlet: HTMLElement,
  path: string,
): void {
  const context = readStaffAppContext();
  if (!context) {
    window.location.hash = '/';
    return;
  }

  const sidebar = el('aside', { class: 'app-nav', 'aria-label': 'Staff' });
  sidebar.append(
    brand(context),
    locationSwitcher(context),
    primaryNav(path),
    actorCard(context),
    utilityNav(path),
    personaSwitch(context),
  );

  const top = el(
    'header',
    { class: 'app-top' },
    el('h1', { class: 'app-top__title' }, pageTitle(path)),
    el(
      'div',
      { class: 'app-top__meta' },
      el(
        'p',
        { class: 'app-top__location', 'data-slot': 'chrome-location' },
        locationLabel(context.location),
      ),
      renderStatusHeaderBadge(),
    ),
  );

  const banner = el(
    'div',
    { class: 'banner banner--app', role: 'status' },
    el('strong', {}, 'Preview environment.'),
    ' Seed locations and roles only. No PHI, no live Stripe, no production clinic traffic.',
  );

  const main = el('div', { class: 'app-main' }, top, banner, outlet);
  const root = el('div', { class: 'app' }, sidebar, main);
  host.replaceChildren(root);
}

function brand(context: StaffAppContext): HTMLElement {
  return el(
    'a',
    { class: 'app-brand', href: '#/calendar' },
    el('span', { class: 'brand__mark', 'aria-hidden': 'true' }, 'D'),
    el(
      'span',
      { class: 'brand__text' },
      el('span', { class: 'brand__name', 'data-slot': 'brand-location' }, context.location.name),
      el('span', { class: 'brand__tag' }, 'Staff preview'),
    ),
  );
}

function locationSwitcher(context: StaffAppContext): HTMLElement {
  const panelId = 'location-switcher-panel';
  const toggle = el(
    'button',
    {
      class: 'loc-switch__toggle',
      type: 'button',
      'aria-expanded': 'false',
      'aria-controls': panelId,
    },
    el('span', { class: 'loc-switch__name' }, context.location.name),
    el('span', { class: 'loc-switch__code' }, context.location.code),
    el('span', { class: 'loc-switch__chevron', 'aria-hidden': 'true' }, '▾'),
  );

  const search = el('input', {
    class: 'loc-switch__search',
    type: 'search',
    placeholder: 'Search locations…',
    'aria-label': 'Search locations',
  }) as HTMLInputElement;

  const list = el('ul', { class: 'loc-switch__list', role: 'listbox', 'aria-label': 'Your Locations' });
  paintLocationList(list, context, '');

  search.addEventListener('input', () => {
    paintLocationList(list, readStaffAppContext() ?? context, search.value);
  });

  const panel = el(
    'div',
    { class: 'loc-switch__panel', id: panelId, hidden: 'true' },
    el('p', { class: 'loc-switch__heading' }, 'Your Locations'),
    search,
    list,
  );

  toggle.addEventListener('click', () => {
    const open = panel.hasAttribute('hidden');
    if (open) {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      search.focus();
    } else {
      panel.setAttribute('hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hasAttribute('hidden')) {
      panel.setAttribute('hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });

  return el('div', { class: 'loc-switch' }, toggle, panel);
}

function paintLocationList(list: HTMLElement, context: StaffAppContext, query: string): void {
  const matches = filterLocations(context.locations, query);
  list.replaceChildren();
  if (matches.length === 0) {
    list.append(el('li', { class: 'loc-switch__empty' }, 'No matching locations'));
    return;
  }
  for (const location of matches) {
    const selected = location.id === context.location.id;
    const option = el(
      'li',
      {},
      el(
        'button',
        {
          class: selected ? 'loc-switch__option loc-switch__option--current' : 'loc-switch__option',
          type: 'button',
          role: 'option',
          'aria-selected': selected ? 'true' : 'false',
          'data-location-id': location.id,
        },
        el('span', { class: 'loc-switch__option-name' }, location.name),
        el('span', { class: 'loc-switch__option-code' }, location.code),
      ),
    );
    option.querySelector('button')?.addEventListener('click', () => {
      selectStaffLocation(location.id);
      notifyStaffContextChanged();
    });
    list.append(option);
  }
}

function primaryNav(path: string): HTMLElement {
  const nav = el('nav', { class: 'app-nav__groups', 'aria-label': 'Staff sections' });
  for (const group of STAFF_NAV_GROUPS) {
    const open = !collapsed.has(group.id);
    const button = el(
      'button',
      {
        class: 'app-nav__group-toggle',
        type: 'button',
        'aria-expanded': open ? 'true' : 'false',
      },
      el('span', {}, group.label),
      el('span', { class: 'app-nav__chevron', 'aria-hidden': 'true' }, open ? '▾' : '▸'),
    );
    const items = el('ul', { class: 'app-nav__items' });
    if (!open) {
      items.setAttribute('hidden', 'true');
    }
    for (const item of group.items) {
      items.append(el('li', {}, navLink(item, path)));
    }
    button.addEventListener('click', () => {
      if (collapsed.has(group.id)) {
        collapsed.delete(group.id);
        items.removeAttribute('hidden');
        button.setAttribute('aria-expanded', 'true');
        button.querySelector('.app-nav__chevron')!.textContent = '▾';
      } else {
        collapsed.add(group.id);
        items.setAttribute('hidden', 'true');
        button.setAttribute('aria-expanded', 'false');
        button.querySelector('.app-nav__chevron')!.textContent = '▸';
      }
    });
    nav.append(el('div', { class: 'app-nav__group' }, button, items));
  }
  return nav;
}

function utilityNav(path: string): HTMLElement {
  const nav = el('nav', { class: 'app-nav__utility', 'aria-label': 'Account' });
  for (const item of STAFF_UTILITY_ITEMS) {
    nav.append(navLink(item, path));
  }
  const signOut = el('button', { class: 'app-nav__link app-nav__signout', type: 'button' }, 'Sign out');
  signOut.addEventListener('click', () => {
    signOutStaffApp();
  });
  nav.append(signOut);
  return nav;
}

function navLink(item: StaffNavItem, path: string): HTMLAnchorElement {
  const link = el(
    'a',
    {
      class: 'app-nav__link',
      href: `#${item.path}`,
      'data-nav': item.path,
    },
    item.label,
  );
  link.prepend(navIcon(item.icon));
  link.setAttribute('aria-current', item.path === path ? 'page' : 'false');
  return link;
}

function actorCard(context: StaffAppContext): HTMLElement {
  return el(
    'div',
    { class: 'app-actor' },
    el('span', { class: 'app-actor__avatar', 'aria-hidden': 'true' }, initials(context.actor.displayName)),
    el(
      'span',
      { class: 'app-actor__text' },
      el('span', { class: 'app-actor__name' }, context.actor.displayName),
      el('span', { class: 'app-actor__role' }, context.actor.roleTitle),
    ),
  );
}

function personaSwitch(context: StaffAppContext): HTMLElement {
  const field = el('div', { class: 'persona' }, el('p', { class: 'persona__label' }, 'Preview as'));
  for (const persona of ['admin', 'owner'] as const) {
    const active = context.actor.persona === persona;
    const button = el(
      'button',
      {
        class: active ? 'persona__btn persona__btn--on' : 'persona__btn',
        type: 'button',
        'aria-pressed': active ? 'true' : 'false',
      },
      persona === 'admin' ? 'Admin' : 'Location owner',
    );
    button.addEventListener('click', () => {
      switchStaffPersona(persona);
      notifyStaffContextChanged();
    });
    field.append(button);
  }
  return field;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function pageTitle(path: string): string {
  const labels: Record<string, string> = {
    '/calendar': 'Calendar',
    '/patients': 'Patients',
    '/chart': 'Chart / EMR',
    '/service-center': 'Service Center',
    '/orders': 'Sales & Order History',
    '/checkout': 'Checkout',
    '/admin': 'Staff & locations',
    '/provider': 'Provider home',
    '/md-oversight': 'Oversight home',
    '/profile': 'My Profile',
    '/support': 'Support',
  };
  return labels[path] ?? 'Staff';
}

export function bindStaffContextRepaint(paint: () => void): void {
  window.addEventListener(CONTEXT_CHANGE_EVENT, paint);
}

export function setPreviewPersonaAndEnter(persona: PreviewPersona): void {
  switchStaffPersona(persona);
  window.location.hash = '/calendar';
}
